import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "../../context/GameContext.jsx";
import { useLanguage } from "../../hooks/useLanguage.js";
import { ROLE_DATA } from "../../data/roles.js";
import { scaleIn } from "../../animations/variants.js";
import { Btn } from "../ui/Btn.jsx";
import { AutoSkipCountdown } from "../ui/AutoSkipCountdown.jsx";
import { PlayerSelectCard } from "../player/PlayerSelectCard.jsx";
import { ClickablePlayer } from "../player/ClickablePlayer.jsx";
import { NightRoleWrapper } from "./NightRoleWrapper.jsx";

// ── Protector ─────────────────────────────────────────────────────
export function ProtectorActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const [selected, setSelected] = useState(null);
  const alive = session.players.filter(p => p.alive);
  const lastProtected = session.protector_lastProtected;
  const elderPenalty = session.elderPenaltyActive;
  const continueNight = () => dispatch({ type: "NEXT_NIGHT_ROLE" });

  if (elderPenalty) return (
    <NightRoleWrapper roleId="protector" t={t}>
      <div className="bg-stone-900/60 border border-stone-700/30 rounded-2xl p-3 text-center">
        <p className="text-stone-400/80 text-xs">{t("Pouvoir perdu — Ancien éliminé.","Power lost — Elder eliminated.")}</p>
      </div>
      <AutoSkipCountdown onSkip={continueNight} t={t} />
    </NightRoleWrapper>
  );

  return (
    <NightRoleWrapper roleId="protector" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">
        {t("Choisissez un joueur à protéger cette nuit.","Choose a player to protect tonight.")}
        {lastProtected && <span className="block text-indigo-500/60 text-xs mt-1">{t("Même joueur interdit deux nuits de suite.","Cannot protect the same player twice in a row.")}</span>}
      </p>
      <div className="flex flex-col gap-2">
        {alive.map(p => <PlayerSelectCard key={p.id} player={p} selected={selected===p.id} onSelect={setSelected} disabled={p.id===lastProtected} />)}
      </div>
      <div className="flex gap-2">
        <Btn variant="ghost" onClick={continueNight} className="flex-1">{t("Passer","Skip")}</Btn>
        <Btn variant="night" onClick={() => { dispatch({type:"APPLY_PROTECTION",payload:selected}); continueNight(); }} disabled={!selected} className="flex-1">{t("Protéger ✓","Protect ✓")}</Btn>
      </div>
    </NightRoleWrapper>
  );
}

// ── Werewolves ────────────────────────────────────────────────────
export function WerewolfActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const [selected, setSelected] = useState(null);
  const targets = session.players.filter(p => p.alive && p.effectiveAlignment !== "werewolf");

  return (
    <NightRoleWrapper roleId="werewolf" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">{t("Les Loups-Garous désignent leur victime.","The Werewolves designate their victim.")}</p>
      <div className="flex flex-col gap-2">
        {targets.map(p => <PlayerSelectCard key={p.id} player={p} selected={selected===p.id} onSelect={setSelected} />)}
      </div>
      {targets.length===0 && <p className="text-red-400/70 text-sm text-center italic">{t("Aucune cible disponible.","No targets available.")}</p>}
      <Btn variant="wolf" onClick={() => { dispatch({type:"SET_NIGHT_VICTIM",payload:selected}); dispatch({type:"NEXT_NIGHT_ROLE"}); }} disabled={!selected} className="w-full">
        {t("🐺 Confirmer l'attaque","🐺 Confirm attack")}
      </Btn>
    </NightRoleWrapper>
  );
}

// ── Father of Wolves ──────────────────────────────────────────────
export function FatherOfWolvesActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const victimId = session.nightVictim;
  const victim = session.players.find(p => p.id === victimId);
  const canInfect = !session.fatherUsedInfection;
  const alreadyAttempted = session.infectionAttempted;
  const night = session.night;
  const blockedBySettings = night === 1 && !session.gameSettings?.fatherInfectionOnNight1;
  const elderPenalty = session.elderPenaltyActive;
  const continueNight = () => dispatch({ type: "NEXT_NIGHT_ROLE" });
  const infect = () => { dispatch({type:"APPLY_INFECTION",payload:victimId}); continueNight(); };
  const devour = () => continueNight();

  if (elderPenalty) return (
    <NightRoleWrapper roleId="father_of_wolves" t={t}>
      <div className="bg-stone-900/60 border border-stone-700/30 rounded-2xl p-3 text-center">
        <p className="text-stone-400/80 text-xs">{t("Pouvoir perdu — Ancien éliminé.","Power lost — Elder eliminated.")}</p>
      </div>
      <AutoSkipCountdown onSkip={continueNight} t={t} />
    </NightRoleWrapper>
  );

  return (
    <NightRoleWrapper roleId="father_of_wolves" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">{t("Le Loup Père Infect se réveille seul.","The Father of Wolves wakes alone.")}</p>
      {victim && (
        <div className="bg-red-950/40 border border-red-800/40 rounded-2xl p-3 text-center">
          <p className="text-red-300/60 text-xs uppercase tracking-wider mb-1">{t("Victime désignée","Designated Victim")}</p>
          <div className="flex justify-center"><ClickablePlayer player={victim} className="text-red-200 font-bold text-lg" /></div>
        </div>
      )}
      {!victim && <p className="text-indigo-400/60 text-sm text-center italic">{t("Aucune victime cette nuit.","No victim tonight.")}</p>}
      {blockedBySettings && (
        <><div className="bg-stone-900/60 border border-stone-700/30 rounded-2xl p-3 text-center"><p className="text-stone-400 text-xs">{t("Infection désactivée la première nuit (réglages).","Infection disabled on Night 1 (settings).")}</p></div>
        <Btn variant="ghost" onClick={continueNight} className="w-full">{t("Continuer","Continue")}</Btn></>
      )}
      {!blockedBySettings && !canInfect && (
        <><p className="text-indigo-500/60 text-xs text-center italic">{t("Pouvoir d'infection déjà utilisé.","Infection power already used.")}</p>
        <AutoSkipCountdown onSkip={continueNight} t={t} /></>
      )}
      {!blockedBySettings && canInfect && !alreadyAttempted && victim && (
        <><p className="text-indigo-300/70 text-xs text-center">{t("Infecter la victime ? (pouvoir unique — résolu après la nuit)","Infect the victim? (one-time power — resolved after night)")}</p>
        <div className="flex gap-2">
          <Btn variant="ghost" onClick={devour} className="flex-1">{t("Non, dévorer","No, devour")}</Btn>
          <Btn variant="wolf" onClick={infect} className="flex-1">{t("🦠 Infecter","🦠 Infect")}</Btn>
        </div></>
      )}
      {!blockedBySettings && canInfect && alreadyAttempted && (
        <><p className="text-purple-400/60 text-xs text-center italic">{t("Infection tentée — résultat après la nuit.","Infection attempted — result after night.")}</p>
        <Btn variant="ghost" onClick={continueNight} className="w-full">{t("Continuer","Continue")}</Btn></>
      )}
      {!blockedBySettings && canInfect && !victim && (
        <Btn variant="ghost" onClick={continueNight} className="w-full">{t("Continuer","Continue")}</Btn>
      )}
    </NightRoleWrapper>
  );
}

// ── Witch ─────────────────────────────────────────────────────────
export function WitchActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const victimId = session.nightVictim;
  const victim = session.players.find(p => p.id === victimId);
  const [poisonTarget, setPoisonTarget] = useState(null);
  const [showPoison, setShowPoison] = useState(false);
  const canSave = !session.witch_usedSave;
  const canPoison = !session.witch_usedPoison;
  const alreadySaved = session.witch_savedVictim;
  const elderPenalty = session.elderPenaltyActive;
  const poisonBlockedBySettings = session.night===1 && !session.gameSettings?.witchPoisonOnNight1;
  const alive = session.players.filter(p => p.alive && p.id !== victimId);
  const done = () => dispatch({ type:"NEXT_NIGHT_ROLE" });

  if (elderPenalty) return (
    <NightRoleWrapper roleId="witch" t={t}>
      <div className="bg-stone-900/60 border border-stone-700/30 rounded-2xl p-3 text-center">
        <p className="text-stone-400/80 text-xs">{t("Pouvoirs de la Sorcière perdus (Ancien éliminé).","Witch powers lost (Elder eliminated).")}</p>
      </div>
      <AutoSkipCountdown onSkip={done} t={t} />
    </NightRoleWrapper>
  );

  return (
    <NightRoleWrapper roleId="witch" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">{t("La Sorcière consulte ses potions.","The Witch consults her potions.")}</p>
      {victim ? (
        <div className="bg-violet-950/40 border border-violet-800/30 rounded-2xl p-3">
          <p className="text-violet-300/60 text-xs text-center mb-2">
            {t("Victime des loups","Wolves' victim")}: <ClickablePlayer player={victim} className="text-violet-200 font-bold" />
          </p>
          {alreadySaved ? (
            <p className="text-center text-xs text-emerald-500/70 italic">{t("✓ Potion de vie utilisée cette nuit.","✓ Life potion used tonight.")}</p>
          ) : canSave ? (
            <Btn variant="success" onClick={() => dispatch({type:"APPLY_WITCH_SAVE"})} className="w-full">{t("🧪 Utiliser la potion de vie","🧪 Use life potion")}</Btn>
          ) : (
            <p className="text-center text-xs text-emerald-800/60 italic">{t("Potion de vie épuisée.","Life potion exhausted.")}</p>
          )}
        </div>
      ) : (
        <div className="bg-violet-950/20 border border-violet-900/20 rounded-2xl p-3 text-center">
          <p className="text-violet-500/50 text-xs italic">{t("Aucune victime cette nuit.","No victim tonight.")}</p>
        </div>
      )}
      {poisonBlockedBySettings && canPoison && (
        <div className="bg-stone-900/60 border border-stone-700/30 rounded-2xl p-3 text-center">
          <p className="text-stone-500 text-xs">☠️ {t("Potion de mort indisponible la première nuit (réglages).","Death potion unavailable on Night 1 (settings).")}</p>
        </div>
      )}
      {!poisonBlockedBySettings && canPoison && !showPoison && (
        <Btn variant="danger" onClick={() => setShowPoison(true)} className="w-full">{t("☠️ Utiliser la potion de mort","☠️ Use death potion")}</Btn>
      )}
      {!canPoison && <p className="text-center text-xs text-red-900/50 italic">{t("Potion de mort épuisée.","Death potion exhausted.")}</p>}
      {showPoison && (
        <div className="flex flex-col gap-2">
          <p className="text-red-300/70 text-xs text-center">{t("Qui éliminer ?","Who to eliminate?")}</p>
          {alive.map(p => <PlayerSelectCard key={p.id} player={p} selected={poisonTarget===p.id} onSelect={setPoisonTarget} />)}
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={() => setShowPoison(false)} className="flex-1">{t("Annuler","Cancel")}</Btn>
            <Btn variant="danger" onClick={() => { dispatch({type:"APPLY_WITCH_POISON",payload:poisonTarget}); setShowPoison(false); setPoisonTarget(null); done(); }} disabled={!poisonTarget} className="flex-1">{t("Empoisonner ✓","Poison ✓")}</Btn>
          </div>
        </div>
      )}
      <Btn variant="ghost" onClick={done} className="w-full">{t("Rien faire → Dormir","Do nothing → Sleep")}</Btn>
      {!canSave && !canPoison && <AutoSkipCountdown onSkip={done} t={t} />}
    </NightRoleWrapper>
  );
}

// ── Cupid ─────────────────────────────────────────────────────────
export function CupidActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const [selected, setSelected] = useState([]);
  const alive = session.players.filter(p => p.alive);
  const toggleSelect = (id) => setSelected(prev =>
    prev.includes(id) ? prev.filter(x=>x!==id) : prev.length<2 ? [...prev,id] : prev
  );
  return (
    <NightRoleWrapper roleId="cupid" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">{t("Cupidon désigne deux amoureux (cette nuit seulement).","Cupid designates two lovers (this night only).")}</p>
      <p className="text-indigo-500/60 text-xs text-center">{selected.length}/2 {t("sélectionnés","selected")}</p>
      <div className="flex flex-col gap-2">
        {alive.map(p => (
          <motion.button key={p.id} whileTap={{scale:0.97}} onClick={() => toggleSelect(p.id)}
            className={`flex items-center gap-3 rounded-2xl p-3 border transition-all
              ${selected.includes(p.id) ? "border-pink-500 bg-pink-950/40" : "border-amber-900/20 bg-stone-950/30"}`}>
            <span className="text-xl">{ROLE_DATA[p.baseRoleId]?.emoji||"?"}</span>
            <span className="flex-1 text-left text-amber-100 text-sm font-semibold">{p.name}</span>
            {selected.includes(p.id) && <span className="text-pink-400">💕</span>}
          </motion.button>
        ))}
      </div>
      <div className="flex gap-2">
        <Btn variant="ghost" onClick={() => dispatch({type:"NEXT_NIGHT_ROLE"})} className="flex-1">{t("Passer","Skip")}</Btn>
        <Btn variant="night" onClick={() => { dispatch({type:"LINK_LOVERS",payload:selected}); dispatch({type:"NEXT_NIGHT_ROLE"}); }} disabled={selected.length!==2} className="flex-1">{t("💘 Unir","💘 Unite")}</Btn>
      </div>
    </NightRoleWrapper>
  );
}

// ── Flute Player ──────────────────────────────────────────────────
export function FlutePlayerActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const [selected, setSelected] = useState([]);
  const gs = session.gameSettings || {};
  const night = session.night;
  const charmsRequired = gs.flutePlayerConstantCount !== false
    ? Math.max(1, gs.flutePlayerCharmsPerNight ?? 2)
    : night % 2 === 1 ? 2 : 1;
  const alive = session.players.filter(p => p.alive && p.baseRoleId!=="flute_player" && !p.statuses.includes("charmed"));
  const alreadyCharmed = session.players.filter(p => p.alive && p.statuses.includes("charmed"));
  const canConfirm = selected.length===charmsRequired || (alive.length<=charmsRequired && selected.length>0);
  const toggleSelect = (id) => setSelected(prev =>
    prev.includes(id) ? prev.filter(x=>x!==id) : prev.length<charmsRequired ? [...prev,id] : prev
  );
  const skip = () => dispatch({type:"NEXT_NIGHT_ROLE"});
  return (
    <NightRoleWrapper roleId="flute_player" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">
        {t(`Le Joueur de Flûte envoûte ${charmsRequired} joueur${charmsRequired>1?"s":""}.`,
           `The Flute Player enchants ${charmsRequired} player${charmsRequired>1?"s":""}.`)}
      </p>
      {alreadyCharmed.length>0 && <p className="text-teal-400/60 text-xs text-center">🎵 {alreadyCharmed.length} {t("déjà ensorcelé(s)","already enchanted")}</p>}
      {alive.length>0 ? (
        <>
          <p className="text-indigo-500/60 text-xs text-center">{selected.length}/{alive.length<charmsRequired?alive.length:charmsRequired}</p>
          <div className="flex flex-col gap-2">
            {alive.map(p => (
              <motion.button key={p.id} whileTap={{scale:0.97}} onClick={() => toggleSelect(p.id)}
                className={`flex items-center gap-3 rounded-2xl p-3 border transition-all
                  ${selected.includes(p.id) ? "border-teal-500 bg-teal-950/40" : "border-amber-900/20 bg-stone-950/30"}`}>
                <span className="text-xl">{ROLE_DATA[p.baseRoleId]?.emoji||"?"}</span>
                <span className="flex-1 text-left text-amber-100 text-sm font-semibold">{p.name}</span>
                {selected.includes(p.id) && <span className="text-teal-400">🎵</span>}
              </motion.button>
            ))}
          </div>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={skip} className="flex-1">{t("Passer","Skip")}</Btn>
            <Btn variant="night" onClick={() => { dispatch({type:"CHARM_PLAYERS",payload:selected}); skip(); }} disabled={!canConfirm} className="flex-1">{t("🪗 Envoûter","🪗 Enchant")}</Btn>
          </div>
        </>
      ) : (
        <><p className="text-teal-400/50 text-sm text-center italic">{t("Tous les joueurs sont déjà ensorcelés !","All players are already enchanted!")}</p>
        <AutoSkipCountdown onSkip={skip} t={t} /></>
      )}
    </NightRoleWrapper>
  );
}

// ── Seer ──────────────────────────────────────────────────────────
export function SeerActionView() {
  const { state, dispatch } = useGame();
  const { t, lang } = useLanguage();
  const session = state.session;
  const muteSeer = session.gameSettings?.muteSeer;
  const elderPenalty = session.elderPenaltyActive;
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const targets = session.players.filter(p => p.alive);
  const revealedPlayer = session.players.find(p => p.id===selected);
  const revealedRole = revealedPlayer ? ROLE_DATA[revealedPlayer.baseRoleId] : null;
  const isTransformedWC = revealedPlayer?.baseRoleId==="wild_child" && revealedPlayer?.effectiveAlignment==="werewolf";
  const baseAlignment = isTransformedWC ? "werewolf" : revealedRole?.alignment;
  const campLabel = baseAlignment==="village" ? t("Village","Village") : baseAlignment==="werewolf" ? t("Loup-Garou","Werewolf") : t("Neutre","Neutral");
  const campGradient = baseAlignment==="village" ? "from-emerald-900 to-emerald-950" : baseAlignment==="werewolf" ? "from-red-950 to-stone-950" : "from-teal-950 to-slate-950";
  const campBorder = baseAlignment==="village" ? "border-emerald-600" : baseAlignment==="werewolf" ? "border-red-700" : "border-teal-600";
  const skip = () => dispatch({type:"NEXT_NIGHT_ROLE"});
  const done = () => { setRevealed(false); setSelected(null); skip(); };

  if (elderPenalty) return (
    <NightRoleWrapper roleId="seer" t={t}>
      <div className="bg-stone-900/60 border border-stone-700/30 rounded-2xl p-3 text-center">
        <p className="text-stone-400/80 text-xs">{t("Pouvoir perdu — Ancien éliminé.","Power lost — Elder eliminated.")}</p>
      </div>
      <AutoSkipCountdown onSkip={skip} t={t} />
    </NightRoleWrapper>
  );

  return (
    <NightRoleWrapper roleId="seer" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">{t("La Voyante regarde la carte d'un joueur.","The Seer looks at one player's card.")}</p>
      {muteSeer && <p className="text-indigo-500/50 text-[10px] text-center italic">{t("Mode Voyante aveugle : camp uniquement.","Blind Seer mode: camp only.")}</p>}
      {!revealed ? (
        <>
          <div className="flex flex-col gap-2">
            {targets.map(p => <PlayerSelectCard key={p.id} player={p} selected={selected===p.id} onSelect={setSelected} />)}
          </div>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={skip} className="flex-1">{t("Passer","Skip")}</Btn>
            <Btn variant="night" onClick={() => setRevealed(true)} disabled={!selected} className="flex-1">{t("🔮 Révéler","🔮 Reveal")}</Btn>
          </div>
        </>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div key="seer-reveal" variants={scaleIn} initial="initial" animate="animate" className="flex flex-col gap-3">
            {muteSeer ? (
              <div className={`rounded-2xl p-6 bg-gradient-to-b ${campGradient} border-2 ${campBorder} flex flex-col items-center gap-2`}>
                <p className="text-white/50 text-xs uppercase tracking-widest">{revealedPlayer?.name}</p>
                <p className="text-white font-bold text-3xl">{campLabel}</p>
              </div>
            ) : (
              <div className={`rounded-2xl p-5 bg-gradient-to-b ${revealedRole?.gradient} border-2 ${revealedRole?.border} flex flex-col items-center gap-3`}>
                <div className="w-16 h-16 rounded-xl bg-black/30 border border-white/10 flex items-center justify-center overflow-hidden">
                  {revealedRole?.imageUrl ? <img src={revealedRole.imageUrl} alt="" className="w-full h-full object-cover" /> : <span className="text-4xl">{revealedRole?.emoji}</span>}
                </div>
                <div className="text-center">
                  <p className="text-white/50 text-xs uppercase tracking-widest mb-0.5">{revealedPlayer?.name}</p>
                  <p className="text-white font-bold text-xl">{lang==="fr" ? revealedRole?.frenchName : revealedRole?.englishName}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${revealedRole?.badge} text-white/80`}>{campLabel}</span>
                </div>
              </div>
            )}
            <p className="text-indigo-400/50 text-xs text-center italic">{t("Montrez cet écran à la Voyante discrètement.","Show this screen to the Seer discreetly.")}</p>
            <Btn variant="night" onClick={done} className="w-full">{t("Cacher → Continuer","Hide → Continue")}</Btn>
          </motion.div>
        </AnimatePresence>
      )}
    </NightRoleWrapper>
  );
}

// ── Raven ─────────────────────────────────────────────────────────
export function RavenActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const elderPenalty = session.elderPenaltyActive;
  const [selected, setSelected] = useState(null);
  const targets = session.players.filter(p => p.alive);
  const skip = () => dispatch({type:"NEXT_NIGHT_ROLE"});

  if (elderPenalty) return (
    <NightRoleWrapper roleId="raven" t={t}>
      <div className="bg-stone-900/60 border border-stone-700/30 rounded-2xl p-3 text-center">
        <p className="text-stone-400/80 text-xs">{t("Pouvoir perdu — Ancien éliminé.","Power lost — Elder eliminated.")}</p>
      </div>
      <AutoSkipCountdown onSkip={skip} t={t} />
    </NightRoleWrapper>
  );

  return (
    <NightRoleWrapper roleId="raven" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">{t("Le Corbeau désigne un joueur. Il aura +2 votes au prochain scrutin.","The Raven marks a player. They will start the next vote with +2 votes.")}</p>
      <div className="flex flex-col gap-2">
        {targets.map(p => <PlayerSelectCard key={p.id} player={p} selected={selected===p.id} onSelect={setSelected} />)}
      </div>
      <div className="flex gap-2">
        <Btn variant="ghost" onClick={skip} className="flex-1">{t("Passer","Skip")}</Btn>
        <Btn variant="night" onClick={() => { dispatch({type:"APPLY_RAVEN_MARK",payload:selected}); skip(); }} disabled={!selected} className="flex-1">{t("🦅 Marquer","🦅 Mark")}</Btn>
      </div>
    </NightRoleWrapper>
  );
}

// ── Thief ─────────────────────────────────────────────────────────
export function ThiefActionView() {
  const { state, dispatch } = useGame();
  const { t, lang } = useLanguage();
  const session = state.session;
  const thief = session.players.find(p => p.baseRoleId==="thief" && p.alive);
  const [selected, setSelected] = useState(null);
  const targets = session.players.filter(p => p.alive && p.id!==thief?.id);
  const selectedPlayer = session.players.find(p => p.id===selected);
  const selectedRole = selectedPlayer ? ROLE_DATA[selectedPlayer.baseRoleId] : null;
  const skip = () => dispatch({type:"NEXT_NIGHT_ROLE"});
  return (
    <NightRoleWrapper roleId="thief" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">{t("Le Voleur choisit un joueur et vole son rôle. Ce joueur devient Villageois.","The Thief chooses a player and steals their role. That player becomes a Villager.")}</p>
      {selected && selectedRole && (
        <motion.div initial={{opacity:0}} animate={{opacity:1}} className={`rounded-2xl p-3 bg-gradient-to-r ${selectedRole.gradient} border ${selectedRole.border} flex items-center gap-3`}>
          <span className="text-2xl">{selectedRole.emoji}</span>
          <div>
            <p className="text-white/50 text-[10px]">{t("Rôle volé","Stolen role")}</p>
            <p className="text-white font-bold text-sm">{lang==="fr" ? selectedRole.frenchName : selectedRole.englishName}</p>
          </div>
        </motion.div>
      )}
      <div className="flex flex-col gap-2">
        {targets.map(p => <PlayerSelectCard key={p.id} player={p} selected={selected===p.id} onSelect={setSelected} />)}
      </div>
      <div className="flex gap-2">
        <Btn variant="ghost" onClick={skip} className="flex-1">{t("Ne pas voler","Don't steal")}</Btn>
        <Btn variant="night" onClick={() => { dispatch({type:"STEAL_ROLE",payload:{thiefId:thief.id,targetId:selected}}); skip(); }} disabled={!selected} className="flex-1">{t("🃏 Voler le rôle","🃏 Steal role")}</Btn>
      </div>
    </NightRoleWrapper>
  );
}

// ── Wild Child ────────────────────────────────────────────────────
export function WildChildActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const wildChild = session.players.find(p => p.baseRoleId==="wild_child" && p.alive);
  const [selected, setSelected] = useState(null);
  const targets = session.players.filter(p => p.alive && p.id!==wildChild?.id);
  return (
    <NightRoleWrapper roleId="wild_child" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">{t("L'Enfant Sauvage choisit son modèle. Si ce joueur meurt, l'Enfant rejoindra les loups.","The Wild Child chooses a role model. If that player dies, the Wild Child joins the wolves.")}</p>
      <div className="flex flex-col gap-2">
        {targets.map(p => <PlayerSelectCard key={p.id} player={p} selected={selected===p.id} onSelect={setSelected} />)}
      </div>
      <div className="flex gap-2">
        <Btn variant="ghost" onClick={() => dispatch({type:"NEXT_NIGHT_ROLE"})} className="flex-1">{t("Passer","Skip")}</Btn>
        <Btn variant="night" onClick={() => { dispatch({type:"SET_WILD_CHILD_MODEL",payload:selected}); dispatch({type:"NEXT_NIGHT_ROLE"}); }} disabled={!selected} className="flex-1">{t("🧒 Choisir ce modèle","🧒 Choose as model")}</Btn>
      </div>
    </NightRoleWrapper>
  );
}

// ── Big Bad Wolf ──────────────────────────────────────────────────
export function BigBadWolfActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const [selected, setSelected] = useState(null);
  const originalWolfIds = session.originalWolfIds || [];
  const allOriginalWolvesAlive = originalWolfIds.every(id => session.players.find(x=>x.id===id)?.alive);
  const targets = session.players.filter(p => p.alive && p.effectiveAlignment!=="werewolf" && p.id!==session.nightVictim);
  const skip = () => dispatch({type:"NEXT_NIGHT_ROLE"});
  return (
    <NightRoleWrapper roleId="big_bad_wolf" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">{t("Le Grand Méchant Loup peut frapper une victime supplémentaire.","The Big Bad Wolf may strike an additional victim.")}</p>
      {!allOriginalWolvesAlive ? (
        <><div className="bg-stone-900/60 border border-stone-700/30 rounded-2xl p-3 text-center">
          <p className="text-stone-400/80 text-xs">{t("Pouvoir perdu — un loup originel est mort.","Power lost — an original Werewolf has died.")}</p>
        </div><Btn variant="ghost" onClick={skip} className="w-full">{t("Continuer","Continue")}</Btn></>
      ) : targets.length===0 ? (
        <><p className="text-indigo-400/60 text-sm text-center italic">{t("Aucune cible disponible.","No targets available.")}</p>
        <Btn variant="ghost" onClick={skip} className="w-full">{t("Continuer","Continue")}</Btn></>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {targets.map(p => <PlayerSelectCard key={p.id} player={p} selected={selected===p.id} onSelect={setSelected} />)}
          </div>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={skip} className="flex-1">{t("Passer","Skip")}</Btn>
            <Btn variant="wolf" onClick={() => { dispatch({type:"APPLY_BBW_KILL",payload:selected}); skip(); }} disabled={!selected} className="flex-1">{t("🐗 Attaquer","🐗 Attack")}</Btn>
          </div>
        </>
      )}
    </NightRoleWrapper>
  );
}

// ── White Werewolf ────────────────────────────────────────────────
export function WhiteWerewolfActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const [selected, setSelected] = useState(null);
  const ww = session.players.find(p => p.baseRoleId==="white_werewolf" && p.alive);
  const targets = session.players.filter(p => p.alive && p.effectiveAlignment==="werewolf" && p.id!==ww?.id);
  const skip = () => dispatch({type:"NEXT_NIGHT_ROLE"});
  return (
    <NightRoleWrapper roleId="white_werewolf" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">{t("Le Loup Blanc peut éliminer un Loup-Garou cette nuit.","The White Werewolf may eliminate a Werewolf tonight.")}</p>
      {targets.length===0 ? (
        <><p className="text-slate-400/60 text-sm text-center italic">{t("Aucun loup disponible à éliminer.","No wolves available to eliminate.")}</p>
        <Btn variant="ghost" onClick={skip} className="w-full">{t("Continuer","Continue")}</Btn></>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {targets.map(p => <PlayerSelectCard key={p.id} player={p} selected={selected===p.id} onSelect={setSelected} />)}
          </div>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={skip} className="flex-1">{t("Passer","Skip")}</Btn>
            <Btn variant="danger" onClick={() => { dispatch({type:"APPLY_WW_KILL",payload:selected}); skip(); }} disabled={!selected} className="flex-1">{t("🤍 Éliminer","🤍 Eliminate")}</Btn>
          </div>
        </>
      )}
    </NightRoleWrapper>
  );
}

// ── Fox ───────────────────────────────────────────────────────────
// The Fox never sees a verdict. The moderator selects a target; the
// camp-smell check happens silently inside RESOLVE_NIGHT. No popup,
// no "there is/isn't a werewolf" message — ever.
export function FoxActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const fox = session.players.find(p => p.baseRoleId === "fox" && p.alive);
  const [selected, setSelected] = useState(null);
  const targets = session.players.filter(p => p.alive);
  const skip = () => dispatch({ type: "NEXT_NIGHT_ROLE" });
  const confirm = () => {
    dispatch({ type: "SET_FOX_CHECK", payload: selected });
    skip();
  };

  return (
    <NightRoleWrapper roleId="fox" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">
        {t("Le Renard désigne un joueur. Son flair examine ce joueur et ses deux voisins directs.",
           "The Fox designates a player. Its sense of smell examines that player and their two direct neighbors.")}
      </p>
      <p className="text-indigo-500/50 text-[10px] text-center italic">
        {t("Aucun résultat ne sera révélé. Le Renard saura s'il garde son pouvoir uniquement en se réveillant (ou non) la nuit prochaine.",
           "No result will be revealed. The Fox will only know if it kept its power by waking up (or not) next night.")}
      </p>
      <div className="flex flex-col gap-2">
        {targets.map(p => <PlayerSelectCard key={p.id} player={p} selected={selected===p.id} onSelect={setSelected} />)}
      </div>
      <div className="flex gap-2">
        <Btn variant="ghost" onClick={skip} className="flex-1">{t("Passer","Skip")}</Btn>
        <Btn variant="night" onClick={confirm} disabled={!selected} className="flex-1">{t("🦊 Flairer","🦊 Smell")}</Btn>
      </div>
    </NightRoleWrapper>
  );
}

// ── Shepherd ──────────────────────────────────────────────────────
export function ShepherdActionView() {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const session = state.session;
  const elderPenalty = session.elderPenaltyActive;
  const sheepRemaining = session.sheepRemaining ?? 0;
  const alive = session.players.filter(p => p.alive);
  const skip = () => dispatch({ type: "NEXT_NIGHT_ROLE" });
  const give = (playerId) => dispatch({ type: "GIVE_SHEEP", payload: playerId });

  if (elderPenalty) return (
    <NightRoleWrapper roleId="shepherd" t={t}>
      <div className="bg-stone-900/60 border border-stone-700/30 rounded-2xl p-3 text-center">
        <p className="text-stone-400/80 text-xs">{t("Pouvoir perdu — Ancien éliminé.","Power lost — Elder eliminated.")}</p>
      </div>
      <AutoSkipCountdown onSkip={skip} t={t} />
    </NightRoleWrapper>
  );

  return (
    <NightRoleWrapper roleId="shepherd" t={t}>
      <p className="text-indigo-300/80 text-sm text-center">
        {t("Le Berger peut distribuer des moutons. Un mouton sauve son propriétaire d'une attaque des loups.",
           "The Shepherd may distribute sheep. A sheep saves its owner from a Werewolf attack.")}
      </p>
      <div className="bg-stone-900/50 border border-stone-700/30 rounded-2xl p-3 text-center">
        <p className="text-stone-300 text-sm font-semibold">🐑 {t("Moutons restants","Sheep remaining")}: {sheepRemaining}</p>
      </div>
      {sheepRemaining > 0 ? (
        <div className="flex flex-col gap-2">
          {alive.map(p => (
            <div key={p.id} className="flex items-center gap-3 rounded-2xl p-3 border border-amber-900/20 bg-stone-950/30">
              <span className="text-xl">{ROLE_DATA[p.baseRoleId]?.emoji || "?"}</span>
              <div className="flex-1">
                <p className="text-amber-100 text-sm font-semibold">{p.name}</p>
                {(p.sheepCount || 0) > 0 && <p className="text-stone-400 text-[10px]">+{p.sheepCount}🐑</p>}
              </div>
              <button onClick={() => give(p.id)}
                className="text-xs px-3 py-1.5 rounded-xl bg-stone-700/60 border border-stone-500/40
                  text-stone-200 font-semibold active:scale-95 transition-all">
                +1🐑
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-stone-500/60 text-sm text-center italic">
          {t("Plus de moutons disponibles.","No more sheep available.")}
        </p>
      )}
      <Btn variant="ghost" onClick={skip} className="w-full">{t("Continuer","Continue")}</Btn>
      {sheepRemaining === 0 && <AutoSkipCountdown onSkip={skip} t={t} />}
    </NightRoleWrapper>
  );
}

